import { useState } from 'react';
import { dateToStoreISO } from '../utils/timezones';

// Pre-client UX hardening (fechas): captura de fecha+hora EN HORA DE LA TIENDA con el patrón
// consciente ya probado (chips Hoy / Ayer / Otra fecha + eco legible en español) — sustituye a los
// `datetime-local` nativos del corte, que mostraban "mm/dd/yyyy --:-- --" (orden mes/día ambiguo
// para usuarios mexicanos) y arrancaban vacíos. A diferencia de DateTimeField (que emite un ISO en
// hora del dispositivo, FI-10), aquí el VALOR es la hora de pared de la tienda ('YYYY-MM-DDTHH:mm')
// — exactamente el contrato que ShiftClosePage ya convierte con storeWallTimeToIso — y "Hoy"/"Ayer"
// son el día de la TIENDA, nunca el del dispositivo.

const DAY_FMT = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const TIME_FMT = new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
const cap = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

// Formatea la hora de pared LITERAL (sin reinterpretarla en la zona del dispositivo): se parsea
// como si fuera UTC y se formatea en UTC, así "14:30" siempre se lee "2:30 p.m.".
function formatWall(wall: string): string {
  const date = new Date(`${wall}:00Z`);
  if (Number.isNaN(date.getTime())) return wall;
  return `${cap(DAY_FMT.format(date))}, ${TIME_FMT.format(date)}`;
}

export function WallDateTimeField({ label, value, onChange, timeZone, disabled = false }: {
  label: string;
  /** Hora de pared de la tienda 'YYYY-MM-DDTHH:mm' (o '' mientras no hay propuesta). */
  value: string;
  onChange: (wall: string) => void;
  timeZone: string;
  disabled?: boolean;
}) {
  const todayIso = dateToStoreISO(new Date(), timeZone);
  const yesterdayIso = dateToStoreISO(new Date(Date.now() - 86_400_000), timeZone);
  const dateStr = value.slice(0, 10);
  const timeStr = value.slice(11, 16);
  // "Otra fecha" despliega el calendario sin cambiar el valor (igual que DateTimeField); si la
  // fecha ya no es hoy/ayer, el calendario se muestra solo.
  const [otherOpen, setOtherOpen] = useState(false);
  const mode: 'today' | 'yesterday' | 'other' =
    otherOpen || (dateStr !== '' && dateStr !== todayIso && dateStr !== yesterdayIso) ? 'other'
      : dateStr === yesterdayIso ? 'yesterday' : 'today';

  const setDate = (nextDate: string) => onChange(`${nextDate}T${timeStr || '00:00'}`);
  const setTime = (nextTime: string) => onChange(`${dateStr || todayIso}T${nextTime || '00:00'}`);

  return (
    <div className="dtf field">
      <span>{label}</span>
      <div className="dtf__row">
        <button type="button" disabled={disabled} className={`dtf__chip ${mode === 'today' ? 'dtf__chip--active' : ''}`} onClick={() => { setOtherOpen(false); setDate(todayIso); }}>Hoy</button>
        <button type="button" disabled={disabled} className={`dtf__chip ${mode === 'yesterday' ? 'dtf__chip--active' : ''}`} onClick={() => { setOtherOpen(false); setDate(yesterdayIso); }}>Ayer</button>
        <button type="button" disabled={disabled} className={`dtf__chip ${mode === 'other' ? 'dtf__chip--active' : ''}`} onClick={() => setOtherOpen(true)}>Otra fecha</button>
        {mode === 'other' && <input aria-label={`Fecha de ${label.toLowerCase()}`} className="dtf__date" disabled={disabled} max={todayIso} type="date" value={dateStr} onChange={(event) => setDate(event.target.value)} />}
        <input aria-label={`Hora de ${label.toLowerCase()}`} className="dtf__time" disabled={disabled} type="time" value={timeStr} onChange={(event) => setTime(event.target.value)} />
      </div>
      <div className="dtf__echo">{value ? <>Se usará: <strong>{formatWall(value)}</strong> (hora de la tienda)</> : 'Sin capturar todavía.'}</div>
    </div>
  );
}
